import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const PageMetaContext = createContext(null);

export const PageMetaProvider = ({ children }) => {
  const [meta, setMeta] = useState({ timestamp: null, testId: null });
  const setLastUpdated = useCallback((timestamp, testId) => {
    setMeta((prev) => (prev.timestamp === timestamp && prev.testId === testId ? prev : { timestamp, testId }));
  }, []);
  const value = useMemo(() => ({ meta, setLastUpdated }), [meta, setLastUpdated]);
  return <PageMetaContext.Provider value={value}>{children}</PageMetaContext.Provider>;
};

// Called by a page to publish its "last updated" timestamp up to the shared layout header.
// Uses a ref for the setter so the effects never depend on the (per-render) context object,
// which previously caused an infinite update loop between pages and the layout header.
export const usePageLastUpdated = (timestamp, testId) => {
  const ctx = useContext(PageMetaContext);
  const setterRef = useRef(ctx?.setLastUpdated);
  setterRef.current = ctx?.setLastUpdated;

  useEffect(() => {
    setterRef.current?.(timestamp, testId);
  }, [timestamp, testId]);

  useEffect(() => {
    return () => setterRef.current?.(null, null);
  }, []);
};

// Called by the layout header to read the currently active page's timestamp.
export const usePageMeta = () => useContext(PageMetaContext)?.meta || { timestamp: null, testId: null };
